import { useState, useEffect } from 'react'
import { PROGRAMS, TRITONHACKS_EVENTS, PAST_WORKSHOPS, EARLY_START_PROJECTS, IMAGES, SITE_INFO } from '../data/content'
import { Link } from 'react-router-dom'

function ProgramsPage() {
    const [workshopIndex, setWorkshopIndex] = useState(0)

    // Auto-rotate workshop carousel every 3.5 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            setWorkshopIndex((prev) => (prev + 1) % PAST_WORKSHOPS.length)
        }, 3500)

        return () => clearInterval(interval)
    }, [])

    return (
        <div className="pt-28 bg-gradient-to-b from-white via-brand-blue-light/10 to-brand-blue-light/30">
            {/* Programs Hero Section */}
            <section className="relative min-h-[60vh] px-4 overflow-hidden">
                {/* Background cloud blobs */}
                <svg
                    className="absolute -top-20 -right-32 w-[700px] h-[500px] text-brand-blue-light opacity-40 -z-10"
                    viewBox="0 0 200 150"
                    fill="currentColor"
                >
                    <ellipse cx="100" cy="80" rx="90" ry="60" />
                    <ellipse cx="60" cy="90" rx="50" ry="40" />
                    <ellipse cx="140" cy="85" rx="55" ry="45" />
                </svg>

                <svg
                    className="absolute top-1/3 -right-20 w-[400px] h-[350px] text-brand-blue-light opacity-50 -z-10"
                    viewBox="0 0 200 150"
                    fill="currentColor"
                >
                    <ellipse cx="100" cy="80" rx="80" ry="55" />
                    <ellipse cx="140" cy="90" rx="50" ry="40" />
                </svg>

                {/* Decorative Elements */}
                <div className="absolute right-32 top-20 hidden lg:block">
                    <div className="w-16 h-16 rounded-full border-2 border-brand-blue"></div>
                </div>
                <div className="absolute right-24 top-32 hidden lg:block">
                    <div className="w-4 h-4 rounded-full bg-brand-blue"></div>
                </div>
                <div className="absolute left-20 top-1/3 hidden lg:block">
                    <div className="w-3 h-3 rounded-full bg-brand-blue opacity-60"></div>
                </div>

                {/* Plus sign - left */}
                <div className="absolute left-14 bottom-1/4 hidden lg:block">
                    <svg className="w-8 h-8 text-brand-blue opacity-50" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                    </svg>
                </div>

                {/* Curved blob - bottom */}
                <svg
                    className="absolute bottom-0 left-0 w-full h-[200px] text-brand-blue-light opacity-50"
                    viewBox="0 0 400 100"
                    fill="currentColor"
                    preserveAspectRatio="none"
                >
                    <ellipse cx="200" cy="100" rx="250" ry="80" />
                </svg>

                {/* Curved line - right side */}
                <svg
                    className="absolute right-0 top-1/2 w-[200px] h-[400px] text-brand-blue opacity-30 hidden lg:block"
                    viewBox="0 0 100 200"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                >
                    <path d="M100 0 Q 0 100, 100 200" />
                </svg>

                {/* Curved line - left side */}
                <svg
                    className="absolute left-0 top-1/4 w-[150px] h-[300px] text-brand-blue opacity-25 hidden lg:block"
                    viewBox="0 0 100 200"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                >
                    <path d="M0 0 Q 100 100, 0 200" />
                </svg>

                <div className="max-w-[1500px] mx-auto grid lg:grid-cols-2 gap-16 items-center px-6">
                    {/* Text Content - Left Side */}
                    <div className="text-center lg:text-left">
                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-brand-blue mb-8">
                            <span className="border-b-4 border-brand-blue pb-2">OUR PROGRAMS</span>
                        </h1>
                        <p className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-xl">
                            Our organization runs 3 different programs to teach and mentor students: Curriculum, TritonHacks, and
                            Early Start. Learn more about each program below!
                        </p>
                    </div>

                    {/* Polaroid Photos - Right Side */}
                    <div className="relative h-[500px] hidden lg:block">
                        <div className="absolute left-0 top-10 transform -rotate-6 bg-white p-3 shadow-xl rounded-sm">
                            <div className="w-64 h-72 bg-gray-200 overflow-hidden">
                                <img src="/images/TritonHacks6.png" alt="Programs event" className="w-full h-full object-cover" />
                            </div>
                            <div className="h-8"></div>
                        </div>
                        <div className="absolute right-0 top-24 transform rotate-6 bg-white p-3 shadow-xl rounded-sm z-10">
                            <div className="w-72 h-64 bg-gray-200 overflow-hidden">
                                <img src="/images/Curriculum5.png" alt="Students at hackathon" className="w-full h-full object-cover" />
                            </div>
                            <div className="h-8"></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Program Cards Section */}
            <section className="relative py-20 px-4 overflow-hidden">
                {/* Decorative elements - right side */}
                <div className="absolute right-16 top-1/4 hidden lg:block">
                    <svg className="w-10 h-10 text-brand-blue" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                    </svg>
                </div>
                <div className="absolute right-24 top-1/3 hidden lg:block">
                    <div className="w-14 h-14 rounded-full border-2 border-brand-blue opacity-50"></div>
                </div>
                <div className="absolute right-20 top-[40%] hidden lg:block">
                    <div className="w-4 h-4 rounded-full bg-brand-blue"></div>
                </div>

                {/* Curved line - right */}
                <svg
                    className="absolute right-0 top-1/2 w-[150px] h-[300px] text-brand-blue opacity-30 hidden lg:block"
                    viewBox="0 0 75 150"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                >
                    <path d="M75 0 Q 0 75, 75 150" />
                </svg>

                {/* Cloud blob - right */}
                <svg
                    className="absolute -right-20 bottom-0 w-[400px] h-[350px] text-brand-blue-light opacity-40 -z-10"
                    viewBox="0 0 200 150"
                    fill="currentColor"
                >
                    <ellipse cx="100" cy="80" rx="90" ry="60" />
                    <ellipse cx="140" cy="90" rx="50" ry="40" />
                </svg>

                <div className="max-w-[1500px] mx-auto px-6">
                    {/* Program Cards Grid */}
                    <div className="grid md:grid-cols-3 gap-10">
                        {/* TritonHacks Card */}
                        <div className="bg-white p-10 rounded-2xl shadow-lg border border-gray-100 text-center flex flex-col items-center">
                            {/* Trident Icon - 3 prong spear */}
                            <div className="mb-6 w-20 h-20 flex items-center justify-center">
                                <svg className="w-20 h-20 text-brand-blue" viewBox="0 0 64 64" fill="currentColor">
                                    {/* Handle/shaft */}
                                    <rect x="29" y="28" width="6" height="32" rx="1" />
                                    {/* Middle prong - tallest */}
                                    <path d="M32 4L27 20H37L32 4Z" />
                                    <rect x="29" y="18" width="6" height="12" />
                                    {/* Left prong */}
                                    <path d="M18 12L14 24H22L18 12Z" />
                                    <rect x="16" y="22" width="4" height="10" />
                                    <path d="M18 32Q18 28 29 28" stroke="currentColor" strokeWidth="4" fill="none" />
                                    {/* Right prong */}
                                    <path d="M46 12L42 24H50L46 12Z" />
                                    <rect x="44" y="22" width="4" height="10" />
                                    <path d="M46 32Q46 28 35 28" stroke="currentColor" strokeWidth="4" fill="none" />
                                </svg>
                            </div>

                            {/* Title */}
                            <h3 className="text-2xl font-heading font-bold text-gray-900 mb-4">
                                TritonHacks
                            </h3>

                            {/* Description */}
                            <p className="text-gray-600 leading-relaxed mb-6">
                                A beginner-friendly high school hackathon that includes project kits, workshops, and mentors.
                            </p>

                            {/* Button */}
                            <Link to="/programs#tritonhacks" className="btn btn-primary-solid rounded-lg text-sm">
                                LEARN MORE
                            </Link>
                        </div>

                        {/* Curriculum Card */}
                        <div className="bg-white p-10 rounded-2xl shadow-lg border border-gray-100 text-center flex flex-col items-center">
                            {/* Laptop Icon */}
                            <div className="mb-6 w-20 h-20 flex items-center justify-center">
                                <svg className="w-20 h-20 text-brand-blue" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="8" y="12" width="48" height="32" rx="2" />
                                    <rect x="12" y="16" width="40" height="24" rx="1" />
                                    <path d="M4 44H60L56 52H8L4 44Z" fill="currentColor" />
                                    <rect x="24" y="20" width="16" height="2" fill="currentColor" />
                                    <rect x="20" y="26" width="24" height="2" fill="currentColor" />
                                    <rect x="22" y="32" width="20" height="2" fill="currentColor" />
                                </svg>
                            </div>

                            {/* Title */}
                            <h3 className="text-2xl font-heading font-bold text-gray-900 mb-4">
                                Curriculum
                            </h3>

                            {/* Description */}
                            <p className="text-gray-600 leading-relaxed mb-6">
                                Runs CS workshops year-round to provide supplementary material to school curriculum.
                            </p>

                            {/* Button */}
                            <Link to="/programs#curriculum" className="btn btn-primary-solid rounded-lg text-sm">
                                LEARN MORE
                            </Link>
                        </div>

                        {/* Early Start Card */}
                        <div className="bg-white p-10 rounded-2xl shadow-lg border border-gray-100 text-center flex flex-col items-center">
                            {/* Hands Icon */}
                            <div className="mb-6 w-20 h-20 flex items-center justify-center">
                                <svg className="w-20 h-20 text-brand-blue" viewBox="0 0 64 64" fill="currentColor">
                                    <path d="M32 8C28 8 24 10 24 14V20C20 20 16 22 16 26V38C16 46 22 52 30 52H34C42 52 48 46 48 38V26C48 22 44 20 40 20V14C40 10 36 8 32 8Z" opacity="0.3" />
                                    <path d="M20 28C18 28 16 30 16 32V40C16 44 18 46 20 46" strokeWidth="2" stroke="currentColor" fill="none" />
                                    <path d="M44 28C46 28 48 30 48 32V40C48 44 46 46 44 46" strokeWidth="2" stroke="currentColor" fill="none" />
                                    <circle cx="28" cy="24" r="4" />
                                    <circle cx="36" cy="24" r="4" />
                                    <path d="M26 36H38" strokeWidth="2" stroke="currentColor" strokeLinecap="round" />
                                </svg>
                            </div>

                            {/* Title */}
                            <h3 className="text-2xl font-heading font-bold text-gray-900 mb-4">
                                Early Start
                            </h3>

                            {/* Description */}
                            <p className="text-gray-600 leading-relaxed mb-6">
                                15-week program that matches high schoolers with UCSD students to work on a CS project.
                            </p>

                            {/* Button */}
                            <Link to="/programs#earlystart" className="btn btn-primary-solid rounded-lg text-sm">
                                LEARN MORE
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Program Details Section */}
            <section className="relative py-20 px-4 overflow-hidden">
                {/* Background cloud blob */}
                <svg
                    className="absolute -left-32 top-0 w-[500px] h-[400px] text-brand-blue-light opacity-30 -z-10"
                    viewBox="0 0 200 150"
                    fill="currentColor"
                >
                    <ellipse cx="100" cy="80" rx="85" ry="55" />
                    <ellipse cx="50" cy="90" rx="45" ry="35" />
                </svg>

                <div className="max-w-[1500px] mx-auto px-6 space-y-24">
                    {/* TritonHacks Detail */}
                    <div id="tritonhacks" className="relative flex flex-col items-center scroll-mt-32">
                        {/* Content */}
                        <div className="text-center max-w-4xl mx-auto">
                            <h3 className="text-3xl md:text-5xl font-heading font-bold text-brand-blue mb-6">
                                TRITONHACKS
                            </h3>
                            <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                TritonHacks is our annual beginner-friendly hackathon designed specifically for high school students
                                who are new to coding. Participants get access to starter project kits, hands-on workshops, and
                                one-on-one mentorship from UCSD students.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 my-12 w-full">
                            {TRITONHACKS_EVENTS.map((event) => (
                                <div key={event.year} className="bg-white p-8 rounded-2xl shadow-md border border-gray-100 hover:shadow-lg transition-all hover:-translate-y-1 flex flex-col items-center">
                                    <div className="w-full h-48 mb-6 overflow-hidden rounded-xl">
                                        <img src={event.image} alt={`TritonHacks ${event.year}`} className="w-full h-full object-cover" />
                                    </div>
                                    <h4 className="font-bold text-brand-blue text-xl mb-4 text-center">TRITONHACKS {event.year}</h4>
                                    <div className="flex flex-col gap-3 w-full items-center">
                                        {event.status ? (
                                            <span className="text-sm font-bold text-gray-500 tracking-tight italic uppercase">
                                                {event.status}
                                            </span>
                                        ) : (
                                            <>
                                                <a href={event.impactReport} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-brand-blue hover:text-blue-700 transition-colors tracking-tight">IMPACT REPORT</a>
                                                <a href={event.projectGallery} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-brand-blue hover:text-blue-700 transition-colors tracking-tight">PROJECT GALLERY</a>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="text-center">
                            <a href="https://tritonhacks.org" target="_blank" rel="noopener noreferrer" className="btn btn-primary-solid px-8 py-3 rounded-xl text-lg">
                                VISIT TRITONHACKS
                            </a>
                        </div>
                    </div>

                    {/* Curriculum Detail */}
                    <div id="curriculum" className="relative flex flex-col items-center scroll-mt-32">
                        {/* Content */}
                        <div className="text-center max-w-4xl mx-auto">
                            <h3 className="text-3xl md:text-5xl font-heading font-bold text-brand-blue mb-6">
                                CURRICULUM
                            </h3>
                            <p className="text-lg text-gray-700 leading-relaxed mb-8">
                                Our Curriculum program provides year-round CS workshops to schools throughout San Diego.
                                We work closely with teachers to develop supplementary materials that complement their
                                existing curriculum.
                            </p>

                            <div className="my-12 relative w-full lg:px-20">
                                <h4 className="text-2xl font-bold font-heading text-brand-blue mb-8">PAST WORKSHOPS</h4>

                                <div className="relative overflow-hidden">
                                    <div
                                        className="flex transition-transform duration-500 ease-in-out"
                                        style={{ transform: `translateX(-${workshopIndex * 100}%)` }}
                                    >
                                        {PAST_WORKSHOPS.map((workshop, idx) => (
                                            <div key={idx} className="w-full shrink-0 px-4">
                                                <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 flex flex-col md:flex-row gap-8 items-center text-left">
                                                    <img src={workshop.image} alt={workshop.title} className="w-full md:w-1/2 h-64 object-cover rounded-2xl" />
                                                    <div className="md:w-1/2">
                                                        <h5 className="font-bold text-gray-900 text-2xl mb-4 leading-tight">{workshop.title}</h5>
                                                        <p className="text-brand-blue font-bold text-base uppercase tracking-widest mb-4">{workshop.subject}</p>
                                                        <p className="text-gray-600 leading-relaxed">Check out our curriculum that we've taught to hundreds of students across San Diego!</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Carousel Controls */}
                                    <div className="flex justify-center gap-6 mt-10">
                                        <button
                                            onClick={() => setWorkshopIndex((prev) => (prev === 0 ? PAST_WORKSHOPS.length - 1 : prev - 1))}
                                            className="w-14 h-14 bg-white rounded-full shadow-lg border border-gray-100 flex items-center justify-center text-brand-blue hover:bg-brand-blue hover:text-white transition-all transform hover:scale-110"
                                            aria-label="Previous workshop"
                                        >
                                            <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 19l-7-7 7-7" /></svg>
                                        </button>
                                        <button
                                            onClick={() => setWorkshopIndex((prev) => (prev === PAST_WORKSHOPS.length - 1 ? 0 : prev + 1))}
                                            className="w-14 h-14 bg-white rounded-full shadow-lg border border-gray-100 flex items-center justify-center text-brand-blue hover:bg-brand-blue hover:text-white transition-all transform hover:scale-110"
                                            aria-label="Next workshop"
                                        >
                                            <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" /></svg>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <Link to="/partners" className="btn btn-primary-solid px-8 py-3 rounded-xl text-lg mt-4">
                                SCHEDULE A WORKSHOP
                            </Link>
                        </div>
                    </div>

                    {/* Early Start Detail */}
                    <div id="earlystart" className="relative flex flex-col items-center scroll-mt-32">
                        {/* Content */}
                        <div className="text-center max-w-4xl mx-auto">
                            <h3 className="text-3xl md:text-5xl font-heading font-bold text-brand-blue mb-6">
                                EARLY START
                            </h3>
                            <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                Early Start is our 15-week mentorship program that pairs high school students with
                                UCSD student mentors. Together, they work on a computer science project from start to finish.
                            </p>
                            <p className="text-lg text-gray-700 leading-relaxed mb-12">
                                This program gives high schoolers a taste of what it's like to be a CS student in college,
                                while building valuable technical and soft skills along the way.
                            </p>

                            <div className="flex flex-wrap gap-6 justify-center">
                                <a href={`mailto:${SITE_INFO.email}`} className="btn btn-primary-solid px-8 py-3 rounded-xl text-lg">
                                    GET INVOLVED
                                </a>
                                <Link to="/get-involved" className="btn btn-outline px-8 py-3 rounded-xl text-lg">
                                    INTEREST FORM
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* Early Start Projects Archive */}
                    <div className="mt-16 text-center">
                        <h3 className="text-2xl font-heading font-bold text-brand-blue mb-8">
                            COHORT PROJECTS SPRING 2022
                        </h3>
                        <div className="grid md:grid-cols-2 gap-10 max-w-4xl mx-auto text-left">
                            {EARLY_START_PROJECTS.map((project, index) => (
                                <div key={index} className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 flex flex-col items-center group hover:shadow-xl transition-all">
                                    <div className="w-full h-48 bg-gray-200 rounded-xl mb-6 overflow-hidden">
                                        <img src={project.image} alt={project.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    </div>
                                    <a href={project.url} target="_blank" rel="noopener noreferrer" className="btn btn-primary-solid w-full text-center">
                                        {project.title}
                                    </a>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="relative py-20 px-4 overflow-hidden bg-gradient-to-b from-transparent to-white">
                {/* Background blob */}
                <svg
                    className="absolute -bottom-20 -right-32 w-[500px] h-[400px] text-brand-blue-light opacity-25 -z-10"
                    viewBox="0 0 200 150"
                    fill="currentColor"
                >
                    <ellipse cx="100" cy="80" rx="90" ry="60" />
                    <ellipse cx="140" cy="90" rx="50" ry="40" />
                </svg>

                <div className="max-w-[1500px] mx-auto px-6 text-center">
                    <h2 className="text-4xl md:text-5xl font-heading font-bold text-brand-blue mb-8">
                        GET INVOLVED
                    </h2>
                    <p className="text-lg md:text-xl text-gray-700 max-w-2xl mx-auto mb-10">
                        Interested in joining one of our programs as a volunteer, mentor, or partner school?
                        We'd love to hear from you!
                    </p>
                    <div className="flex flex-wrap gap-5 justify-center">
                        <Link to="/about" className="btn btn-primary-solid rounded-lg">
                            JOIN OUR TEAM
                        </Link>
                        <Link to="/partners" className="btn btn-outline rounded-lg">
                            PARTNER WITH US
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    )
}

export default ProgramsPage
